import React, { useCallback, useMemo } from 'react'

import { IconButton, Tooltip } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import { useLocation } from 'react-router-dom'

import { GrandTour, Icon, IconTypes } from '@acre/design-system'
import { useUpdateUserMutation } from '@acre/graphql'
import type { ReactGrandTourStep } from '@acre/react-grand-tour'
import { useUserContext } from '@acre/utils'

import usePreviewMode, { buildPreviewModeUrl } from '@broker-crm-common/hooks/usePreviewMode'
import { useBrandName } from '@broker-crm-utils'

import createPreviewModeTour from '../../tours/PreviewModeTour'

const PreviewModeButton = () => {
  const { pathname } = useLocation()
  const { active: previewModeEnabled } = usePreviewMode()
  const user = useUserContext()
  const brand = useBrandName()

  const previewModeTour = useMemo(() => createPreviewModeTour(brand), [brand])

  const handleDisablePreviewModeClick = useCallback(() => {
    window.location.href = buildPreviewModeUrl({ hostname: window.location.hostname, pathname, enable: false })
  }, [pathname])

  const [updateUser] = useUpdateUserMutation()

  const handleClose = useCallback(() => {
    const existingFeatures = user?.first_time_access?.features?.length
      ? user.first_time_access?.features.filter((feature) => feature.feature !== 'PREVIEW')
      : []

    if (user?.id) {
      updateUser({
        variables: {
          userId: user?.id,
          input: {
            first_time_access: {
              features: [
                ...existingFeatures.map((feature) => ({ feature: feature.feature, complete: feature.complete })),
                {
                  feature: 'PREVIEW',
                  complete: true,
                },
              ],
            },
          },
        },
      })
    }

    return true
  }, [user, updateUser])

  const openTour = useMemo(
    () => !user?.first_time_access?.features?.find((feature) => feature.feature === 'PREVIEW')?.complete,
    [user?.first_time_access?.features],
  )

  return previewModeEnabled ? (
    <>
      <Tooltip title={<FormattedMessage id="nav.previewMode.tooltip" />} placement="bottom-end">
        <IconButton id={previewModeTour.Button.selectorToHTML()} disableRipple onClick={handleDisablePreviewModeClick}>
          <Icon key="previewModeIcon" name={IconTypes.Flask} />
        </IconButton>
      </Tooltip>
      <GrandTour
        hidePreviousStepButton
        hideStepButtons
        steps={[previewModeTour.Button] as ReactGrandTourStep[]}
        open={openTour}
        onClose={handleClose}
      />
    </>
  ) : null
}

export default React.memo(PreviewModeButton)
